/**
 * Workflow Phone Verification — main component.
 *
 * Mirrors WorkflowFaceVerification.tsx architecture.
 * The only external input is a phone number (user-entered), so there
 * is no vendor SDK — just submit _input to WE and poll until terminal.
 *
 * States:
 * - initializing: Waiting for IDME_INIT postMessage from parent
 * - ready: Phone entry screen shown, waiting for user to submit
 * - submitting: Calling WE _input API + polling
 * - completed: WE returned COMPLETED, postMessage sent to parent
 * - error: Something failed
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { submitWorkflowInput, getWorkflowExecution } from "../../shared/api/workflowEngine";
import { WorkflowPhoneScreen } from "./WorkflowPhoneScreen";
import { ResendLink } from "./ResendLink";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { logger } from "../../shared/utils/logger";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";

export type WorkflowPhoneState = "initializing" | "ready" | "submitting" | "verifying" | "completed" | "error";

interface InitPayload {
  executionId: string;
  workflowId: number;
  inspectionName: string;
  authToken?: string;
  apiBaseUrl?: string;
}

const DEFAULT_API_BASE = "https://enroll.idme.test/api/workflow";

export function WorkflowPhoneVerification() {
  const { t } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [state, setState] = useState<WorkflowPhoneState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const [submittedPhone, setSubmittedPhone] = useState<string | null>(null);
  const initDataRef = useRef<InitPayload | null>(null);
  const parentOriginRef = useRef<string>("*");

  // Derive and validate parent origin from document.referrer on mount
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[WorkflowPhone] Parent origin validated:", origin);
      } else {
        logger.warn("[WorkflowPhone] Parent origin not in allowlist:", origin);
      }
    } catch {
      logger.warn("[WorkflowPhone] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  // ── Error helper ──────────────────────────────────────────────────────
  const handleVerificationError = useCallback((errorCode: string, message: string) => {
    setError(message);
    setState("error");
    logger.error(`[WorkflowPhone] Error [${errorCode}]:`, message);
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: { errorCode, message, executionId: initDataRef.current?.executionId },
      },
      parentOriginRef.current
    );
  }, []);

  // ── Phone submit flow: _input → poll ─────────────────────────────────
  const handlePhoneSubmit = useCallback(async (phoneNumber: string) => {
    const data = initDataRef.current;
    if (!data) return;

    // intl-tel-input returns E.164 format (+14088934874) — send as-is to WE.
    const normalized = phoneNumber;

    setState("submitting");
    setSubmittedPhone(normalized);

    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;

    try {
      // Match the WE frontend (UxRouter dispatch) body format exactly:
      // { inspectionName, input: { phone_number, inputType, executionId } }
      logger.log("[WorkflowPhone] Submitting _input to WE, phone:", normalized);
      await submitWorkflowInput(
        apiBase,
        data.workflowId,
        data.executionId,
        {
          inspectionName: data.inspectionName,
          input: {
            phone_number: normalized,
            inputType: "phone_number",
            executionId: data.executionId,
          },
        },
        data.authToken
      );

      // Show "Check your phone" while OTP webhook is in flight.
      // WE frontend waits 1500ms before starting to poll — mirror that here.
      setState("verifying");
      await new Promise((r) => setTimeout(r, 1500));

      // Poll until phone step advances (webhook fires → WE transitions to SSN step).
      // Continue while: PENDING_ASYNC | RUNNING | PENDING | WAITING_FOR_INPUT (phone step)
      // Stop when:      WAITING_FOR_INPUT (SSN step) | COMPLETED | FAILED | CANCELLED
      logger.log("[WorkflowPhone] Polling until phone step advances");
      const intervalMs = 2000;
      const timeoutMs = 120_000;
      const deadline = Date.now() + timeoutMs;

      const isPhoneStepActive = (e: typeof exec) =>
        e.status === "PENDING_ASYNC" ||
        e.status === "RUNNING" ||
        e.status === "PENDING" ||
        (e.status === "WAITING_FOR_INPUT" && e.currentInspection === data.inspectionName);

      let exec = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);

      while (isPhoneStepActive(exec) && Date.now() < deadline) {
        logger.log("[WorkflowPhone] Still", exec.status, `(${exec.currentInspection ?? "?"}) — polling`);
        await new Promise((r) => setTimeout(r, intervalMs));
        exec = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);
      }

      if (Date.now() >= deadline) {
        handleVerificationError("poll_timeout", "Verification timed out waiting for phone step");
        return;
      }

      logger.log("[WorkflowPhone] Phone step done, status:", exec.status, `(${exec.currentInspection ?? "?"})`);

      if (exec.status === "FAILED" || exec.status === "CANCELLED") {
        const msg =
          (exec.result?.data as Record<string, unknown>)?.message as string ??
          "Verification failed";
        handleVerificationError("poll_failed", msg);
        return;
      }

      // WAITING_FOR_INPUT (SSN next) or COMPLETED — phone step succeeded either way
      setState("completed");
    } catch (err) {
      logger.error("[WorkflowPhone] Submit flow failed:", err);
      handleVerificationError(
        "submit_failed",
        err instanceof Error ? err.message : "Verification processing failed"
      );
    }
  }, [handleVerificationError]);

  // ── Resend the verification link (user-driven retry) ─────────────────
  // Re-delivers the link for the SAME in-flight verification. The WE worker
  // recognises this by the `resend` inputType and re-sends; no verificationId
  // is needed here — the worker injects it from the paused step's async token.
  // Throws on failure so ResendLink keeps the button visible (a failed resend
  // must not fail the verification — the original link is still valid).
  const handleResend = useCallback(async () => {
    const data = initDataRef.current;
    if (!data) return;
    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;
    logger.log("[WorkflowPhone] Resending link for execution:", data.executionId);
    await submitWorkflowInput(
      apiBase,
      data.workflowId,
      data.executionId,
      {
        inspectionName: data.inspectionName,
        input: { inputType: "resend", executionId: data.executionId },
      },
      data.authToken
    );
  }, []);

  // ── "I don't have a US phone number" → Trusted Referee (VEX-7412) ─────
  // Submit a decline signal so the engine fails the phone step (verified=false),
  // which drains the workflow to _on_error and fires the Trusted Referee redirect.
  // Then surface a semantic errorCode so the host (IPA) routes onward to the TR
  // step — mirroring the consent flow's "user_declined" convention.
  const handleNoUsPhone = useCallback(async () => {
    const data = initDataRef.current;
    if (!data) return;
    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;
    setState("submitting");
    try {
      logger.log("[WorkflowPhone] No US phone — submitting decline to route to Trusted Referee");
      await submitWorkflowInput(
        apiBase,
        data.workflowId,
        data.executionId,
        {
          inspectionName: data.inspectionName,
          input: { captureOutcome: "no-us-phone", executionId: data.executionId },
        },
        data.authToken
      );
    } catch (err) {
      logger.error("[WorkflowPhone] no-us-phone submit failed:", err);
      handleVerificationError(
        "submit_failed",
        err instanceof Error ? err.message : "Verification processing failed"
      );
      return;
    }
    // The engine has failed the phone step → workflow _on_error → Trusted Referee.
    // Hand control back to the host: IPA's onError("no_us_phone") routes to /step,
    // which re-resolves the now-failed execution to the Trusted Referee screen.
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: {
          errorCode: "no_us_phone",
          message: "User reported no US phone number",
          executionId: data.executionId,
        },
      },
      parentOriginRef.current
    );
  }, [handleVerificationError]);

  // ── Step 1: Send IDME_READY, listen for IDME_INIT ────────────────────
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      parentOriginRef.current
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[WorkflowPhone] IDME_INIT rejected from origin:", event.origin);
          return;
        }
      } catch {
        logger.warn("[WorkflowPhone] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const payload = event.data.payload as InitPayload;
      logger.log("[WorkflowPhone] Received init:", payload.executionId);
      setInitData(payload);
      initDataRef.current = payload;
      setState("ready");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Step 2: Send VERIFICATION_COMPLETE when WE completes ─────────────
  useEffect(() => {
    if (state !== "completed" || !initData) return;
    logger.log("[WorkflowPhone] Notifying parent of completion");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: {
          executionId: initData.executionId,
          workflowId: initData.workflowId,
          status: "completed",
          phoneNumber: submittedPhone,
        },
      },
      parentOriginRef.current
    );
  }, [state, initData, submittedPhone]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(' ')}>
      <div className={styles.container}>

        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              {consumerLogo ? (
                <img src={consumerLogo.src} alt={consumerLogo.alt} />
              ) : (
                <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
              )}
            </div>
          </header>
        )}

        <main className={styles.contentContainer}>

          {/* Initializing */}
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — phone entry form */}
          {state === "ready" && (
            <WorkflowPhoneScreen
              onSubmit={handlePhoneSubmit}
              onNoUsPhone={handleNoUsPhone}
              isSubmitting={false}
            />
          )}

          {/* Processing — spinner */}
          {state === "submitting" && (
            <div className={styles.card}>
              <h2 className={styles.title}>{t("phone.processing.title")}</h2>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Verifying — webhook in flight, waiting for OTP mock to fire */}
          {state === "verifying" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
              <h2 className={styles.title}>{t("phone.verifying.title")}</h2>
              <p className={styles.subtitle}>{t("phone.verifying.subtitle")}</p>
              <ResendLink onResend={handleResend} />
            </div>
          )}

          {/* Completed */}
          {state === "completed" && (
            <div className={styles.completedContainer}>
              <h1 className={styles.completedTitle}>{t("phone.completed.title")}</h1>
              <p className={styles.completedSubtitle}>{t("phone.completed.subtitle")}</p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t("phone.error.title")}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </main>

        <footer className={styles.footer}>
          <LanguageSelector />
          <div className={styles.footerLinks}>
            <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink} aria-label={t('phone.footer.whatIsIdmeAriaLabel')}>{t('phone.footer.whatIsIdme')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink} aria-label={t('phone.footer.termsOfServiceAriaLabel')}>{t('phone.footer.termsOfService')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink} aria-label={t('phone.footer.privacyPolicyAriaLabel')}>{t('phone.footer.privacyPolicy')}</a>
          </div>
        </footer>

      </div>
    </div>
  );
}
